import { Button } from '@progress/kendo-react-buttons';
import {
  Card,
  CardBody,
  GridLayout,
  GridLayoutItem,
} from '@progress/kendo-react-layout';
import EvEquipmentIcon from 'assets/images/ic_equipment_ev.svg';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IEquipmentEntry } from 'types/IEquipmentEntry';

import CriticalBateryIcon from 'assets/images/ic_tele_battery_critical_small.svg';
import NormalBateryIcon from 'assets/images/ic_tele_battery_normal_small.svg';
import WarningBateryIcon from 'assets/images/ic_tele_battery_warning_small.svg';

import IntelIcon from 'assets/images/ic_equipment_intellattach_small.svg';

import { useLoadScript } from '@react-google-maps/api';
import { librariesList } from 'app/common/librariesList';
import { isCanada } from 'app/common/websiteCountry';
import { LoadingOverlay } from 'app/components/LoadingOverlay';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import {
  getCanadaFormateDate,
  getUSFormateDate,
} from 'app/pages/TrackMaintenencePage/maintenanceUtils';
import {
  ic_tele_geofence_in_small as MapPinIcon,
  ic_tele_geofence_unavailable as MapUnavailablePinIcon,
} from 'app/widgets/SVGs';
import AssetTriangleIcon from 'assets/images/ic_tele_error.svg';

import { getLocale } from 'locales/utils';
import {
  convertNumberToDistance,
  formatNumberWithDecimals,
  formatSeconds,
} from 'utils/general';
import { StarterStatusRow, SubscriptionRow } from './components';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { useManageYourPlanDialog } from 'context';
import {
  InMotionStatusIcon,
  RestartInhibitStatusIcon,
} from 'app/components/equipment';
import { ChevronRightIcon } from 'app/components/icons';
import { BaseButton } from 'app/components/buttons/BaseButton';
import './EquipmentTelematicDescription.less';
import clsx from 'clsx';
import useSubscriptionInfo from 'app/hooks/subscriptions/useSubscriptionInfo';
import { userSettingsApi } from 'services/api/user-settings/queries';

interface IEquipmentTelematicDescription {
  equipment: IEquipmentEntry;
  showChevron?: boolean;
  onClick?: () => void;
}

export function EquipmentTelematicDescription(
  props: IEquipmentTelematicDescription,
) {
  const { t, i18n } = useTranslation();

  const isSubscriptionsFeatureEnabled = useFlag(FeatureName.SUBSCRIPTIONS);

  const { data: { measurementUnit } = {} } =
    userSettingsApi.useGetUserSettingsQuery();

  const { openDialog: openManageYourPlanDialog } = useManageYourPlanDialog();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_API_KEY as string,
    libraries: librariesList,
  });

  const [adress, setAdress] = React.useState('' as string | null);
  const [adressLoading, setAdressLoading] = React.useState(false);

  const subscriptionInfo = useSubscriptionInfo(props.equipment.subscriptions);

  React.useEffect(() => {
    if (isLoaded && props.equipment.telematics?.location) {
      setAdressLoading(true);
      new google.maps.Geocoder()
        .geocode({
          location: {
            lat: props.equipment.telematics?.location.latitude,
            lng: props.equipment.telematics?.location.longitude,
          },
        })
        .then(result => {
          setAdress(result.results[0].formatted_address);
        })
        .catch(e => {
          setAdress(null);
        })
        .finally(() => {
          setAdressLoading(false);
        });
    }
  }, [isLoaded]);
  const aproximateToTwo = num => {
    return num.toFixed(2);
  };

  const aproximateToTwoHours = num => {
    if (props.equipment.hasTelematics) {
      const currentEngineHours = Math.round(num * 100) / 100;
      return i18n.language === 'fr'
        ? formatNumberWithDecimals(currentEngineHours, 1).replace(
            '.',
            t('hours_separator'),
          )
        : formatNumberWithDecimals(currentEngineHours, 1);
    } else {
      const currentEngineHours = Math.round(num * 100) / 100;
      return i18n.language === 'fr'
        ? formatNumberWithDecimals(currentEngineHours, 2).replace(
            '.',
            t('hours_separator'),
          )
        : formatNumberWithDecimals(currentEngineHours, 2);
    }
  };

  const getEngineHours = () => {
    if (
      props.equipment.telematics?.cumulativeOperatingHours &&
      props.equipment.telematics?.cumulativeOperatingHours > 0
    ) {
      return aproximateToTwoHours(
        props.equipment.telematics.cumulativeOperatingHours,
      );
    }
    if (
      props.equipment.userEnteredEngineHours &&
      props.equipment.userEnteredEngineHours > 0
    ) {
      return aproximateToTwoHours(props.equipment.userEnteredEngineHours);
    }
    return '0';
  };

  const hasFuelOrDef = !!(
    props.equipment.telematics?.fuelRemainingPercent ||
    props.equipment.telematics?.fuelRemainingPercent === 0 ||
    props.equipment.telematics?.defRemainingPercent ||
    props.equipment.telematics?.defRemainingPercent === 0
  );

  const isEV = !!props.equipment.telematics?.electric;

  const locale = getLocale();
  const getDate = () => {
    const locationTime = props.equipment?.telematics?.locationTime || '';

    return isCanada || locale === 'fr-CA' || locale === 'en-CA'
      ? getCanadaFormateDate(locationTime, 'date_time', locale)
      : getUSFormateDate(locationTime, locale);
  };

  const height = 30;
  const marginBottom = 2;
  const batteryPercent =
    props.equipment?.telematics?.electric?.battery.remainingCharge ?? 0;
  const fillColor =
    batteryPercent > 25.99
      ? '#1AAD7F'
      : batteryPercent > 10
      ? '#E88F26'
      : '#FF0700';

  const filledHeight = (batteryPercent / 100) * height;

  const chargerSvg = (
    <g id="icon" transform="translate(6.414 1.899)">
      <g
        id="Icon-2"
        data-name="Icon"
        transform="translate(-1.173 10.737)"
        fill="#131313"
        strokeLinejoin="round"
      >
        <path
          d="M 4.543470859527588 12.63846015930176 L 4.06972074508667 12.5285701751709 L 4.043566703796387 8.458120346069336 L 1.172850728034973 8.458120346069336 L 0.7642907500267029 8.458120346069336 L 0.9502507448196411 8.094330787658691 L 4.769050598144531 0.6234105825424194 L 5.241640567779541 0.7399105429649353 L 5.196420669555664 4.905210494995117 L 5.19642448425293 5.06255054473877 L 7.914650917053223 5.06255054473877 L 8.318540573120117 5.06255054473877 L 8.138410568237305 5.424050331115723 L 4.543470859527588 12.63846015930176 Z"
          stroke="none"
        />
        <path
          d="M 4.319710731506348 12.5269603729248 L 7.914650917053223 5.31255054473877 L 4.946430683135986 5.31255054473877 L 4.946430683135986 4.902500629425049 L 4.991650581359863 0.7372005581855774 L 1.172850728034973 8.208120346069336 L 4.291960716247559 8.208120346069336 L 4.319710731506348 12.5269603729248 M 4.319833755493164 13.02700996398926 C 4.282365798950195 13.02700996398926 4.244472026824951 13.02278518676758 4.206730842590332 13.01403045654297 C 3.981300830841064 12.96174049377441 3.821210861206055 12.76158046722412 3.81972074508667 12.53017044067383 L 3.795162916183472 8.708120346069336 L 1.172850728034973 8.708120346069336 C 0.9987707734107971 8.708120346069336 0.8372207880020142 8.617580413818359 0.7463607788085938 8.469090461730957 C 0.6555007696151733 8.320611000061035 0.6484107375144958 8.135550498962402 0.7276407480239868 7.980550765991211 L 4.546440601348877 0.509630560874939 C 4.652070999145508 0.3029905557632446 4.885999202728271 0.1961905062198639 5.111320972442627 0.2517305612564087 C 5.336650848388672 0.3072805404663086 5.494140625 0.5105705261230469 5.491620540618896 0.7426305413246155 L 5.447436332702637 4.81255054473877 L 7.914650917053223 4.81255054473877 C 8.087821006774902 4.81255054473877 8.248661041259766 4.902150630950928 8.339800834655762 5.049400329589844 C 8.430940628051758 5.196640491485596 8.439400672912598 5.380560398101807 8.362171173095703 5.535550594329834 L 4.767230987548828 12.74996089935303 C 4.681300163269043 12.92240428924561 4.506162643432617 13.02700996398926 4.319833755493164 13.02700996398926 Z"
          stroke="none"
          fill="#fff"
        />
      </g>
    </g>
  );

  const pluggedSvg = (
    <g
      id="Icon"
      transform="translate(6.666 12.999)"
      fill="#1a1a1a"
      strokeLinecap="round"
    >
      <path
        d="M 3.333600044250488 10.25080013275146 C 2.828519821166992 10.25080013275146 2.41759991645813 9.839879989624023 2.41759991645813 9.334799766540527 L 2.41759991645813 8.132722854614258 C 0.8605855703353882 7.722025871276855 -0.2491000592708588 6.296434879302979 -0.2491000592708588 4.667400360107422 L -0.2491000592708588 2.667600154876709 L -0.2491000592708588 2.417600154876709 L 0.0008999400888569653 2.417600154876709 L 0.7507999539375305 2.417600154876709 L 0.7507999539375305 0.6678001880645752 C 0.7507999539375305 0.1622201949357986 1.16211998462677 -0.2490998059511185 1.667699933052063 -0.2490998059511185 C 2.17278003692627 -0.2490998059511185 2.583699941635132 0.1622201949357986 2.583699941635132 0.6678001880645752 L 2.583699941635132 2.417600154876709 L 4.084400177001953 2.417600154876709 L 4.084400177001953 0.6678001880645752 C 4.084400177001953 0.1622201949357986 4.495319843292236 -0.2490998059511185 5.000400066375732 -0.2490998059511185 C 5.505980014801025 -0.2490998059511185 5.917299747467041 0.1622201949357986 5.917299747467041 0.6678001880645752 L 5.917299747467041 2.417600154876709 L 6.667200088500977 2.417600154876709 L 6.917200088500977 2.417600154876709 L 6.917200088500977 2.667600154876709 L 6.917200088500977 4.667400360107422 C 6.917200088500977 6.296175956726074 5.807496070861816 7.721708297729492 4.250499725341797 8.132586479187012 L 4.250499725341797 9.334799766540527 C 4.250499725341797 9.839879989624023 3.839179992675781 10.25080013275146 3.333600044250488 10.25080013275146 Z"
        stroke="none"
      />
      <path
        d="M 3.333600044250488 10.00080013275146 C 3.702600002288818 10.00080013275146 4.000499725341797 9.702899932861328 4.000499725341797 9.334799766540527 L 4.000499725341797 7.934280395507812 C 5.522250175476074 7.625450134277344 6.667200088500977 6.280420303344727 6.667200088500977 4.667400360107422 L 6.667200088500977 2.667600154876709 L 5.667299747467041 2.667600154876709 L 5.667299747467041 0.6678001880645752 C 5.667299747467041 0.2997002005577087 5.368499755859375 0.000900200626347214 5.000400066375732 0.000900200626347214 C 4.632299900054932 0.000900200626347214 4.334400177001953 0.2997002005577087 4.334400177001953 0.6678001880645752 L 4.334400177001953 2.667600154876709 L 2.333699941635132 2.667600154876709 L 2.333699941635132 0.6678001880645752 C 2.333699941635132 0.2997002005577087 2.035799980163574 0.000900200626347214 1.667699933052063 0.000900200626347214 C 1.298699975013733 0.000900200626347214 1.000799894332886 0.2997002005577087 1.000799894332886 0.6678001880645752 L 1.000799894332886 2.667600154876709 L 0.0008999400888569653 2.667600154876709 L 0.0008999400888569653 4.667400360107422 C 0.0008999400888569653 6.28065013885498 1.146139979362488 7.625820159912109 2.66759991645813 7.934400081634521 L 2.66759991645813 9.334799766540527 C 2.66759991645813 9.702899932861328 2.965499877929688 10.00080013275146 3.333600044250488 10.00080013275146 M 3.333600044250488 10.50080013275146 C 2.690670013427734 10.50080013275146 2.16759991645813 9.977729797363281 2.16759991645813 9.334799766540527 L 2.16759991645813 8.320199012756348 C 0.6003711819648743 7.819756984710693 -0.4991000592708588 6.344305038452148 -0.4991000592708588 4.667400360107422 L -0.4991000592708588 2.167600154876709 L 0.5007999539375305 2.167600154876709 L 0.5007999539375305 0.6678001880645752 C 0.5007999539375305 0.02437020093202591 1.024269938468933 -0.4990997910499573 1.667699933052063 -0.4990997910499573 C 2.310629844665527 -0.4990997910499573 2.833699941635132 0.02437020093202591 2.833699941635132 0.6678001880645752 L 2.833699941635132 2.167600154876709 L 3.834399938583374 2.167600154876709 L 3.834399938583374 0.6678001880645752 C 3.834399938583374 0.02437020093202591 4.357470035552979 -0.4990997910499573 5.000400066375732 -0.4990997910499573 C 5.643829822540283 -0.4990997910499573 6.167299747467041 0.02437020093202591 6.167299747467041 0.6678001880645752 L 6.167299747467041 2.167600154876709 L 7.167200088500977 2.167600154876709 L 7.167200088500977 4.667400360107422 C 7.167200088500977 6.3441481590271 6.067867755889893 7.819527149200439 4.500499725341797 8.320037841796875 L 4.500499725341797 9.334799766540527 C 4.500499725341797 9.977729797363281 3.977030038833618 10.50080013275146 3.333600044250488 10.50080013275146 Z"
        stroke="none"
        fill="#fff"
      />
    </g>
  );

  const warningSvg = (
    <>
      <rect id="Box" width="16" height="16" fill="none" />
      <g id="Icon" transform="translate(7 3.5)">
        <g
          id="Path"
          transform="translate(2 10)"
          fill="#ff0700"
          stroke="#fff"
          strokeWidth="0.5"
        >
          <rect width="2" height="7" stroke="none" />
          <rect width="2.5" height="7.5" fill="none" />
        </g>
        <g
          id="Path-2"
          data-name="Path"
          transform="translate(2 20)"
          fill="#ff0700"
          stroke="#fff"
          strokeWidth="0.5"
        >
          <rect width="2" height="2" stroke="none" />
          <rect width="2.5" height="2.5" fill="none" />
        </g>
      </g>
    </>
  );

  const batteryStatusSvg =
    batteryPercent < 10
      ? warningSvg
      : props.equipment?.telematics?.electric?.battery.charging
      ? chargerSvg
      : props.equipment?.telematics?.electric?.battery.chargerPresent
      ? pluggedSvg
      : null;

  const batteryStatusState = props.equipment?.telematics?.electric?.battery
    .fullCharge
    ? t('fully_charged')
    : props.equipment?.telematics?.electric?.battery.charging
    ? ` ${t('until_fully_charged', {
        time: formatSeconds(
          props.equipment?.telematics?.electric?.battery.timeUntilFullCharge,
        ),
      })} `
    : props.equipment?.telematics?.electric?.battery.chargerPresent
    ? t('not_charging')
    : null;
  const batteryHasState = !!batteryStatusState;

  return (
    <>
      <CardBody>
        <div className="row">
          <div className="col-xxs-3">
            <img
              src={
                props.equipment.modelIconUrl ||
                getDefaultThumbnail(
                  props.equipment.type,
                  props.equipment.subcategory,
                )
              }
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = getDefaultThumbnail(
                  props.equipment.type,
                  props.equipment.subcategory,
                );
              }}
              alt=""
              className="equipment-list-image"
            />
          </div>
          <div className="col-xxs-9">
            <div className="equipment-telematic-description__name-block">
              <span
                className="equipment-list-nickname"
                title={props.equipment.nickName || props.equipment.model}
              >
                {isEV ? <img src={EvEquipmentIcon} alt="React Logo" /> : null}
                {!!props.equipment.smartAttachment && (
                  <img style={{ width: '24px' }} src={IntelIcon} alt="Intel" />
                )}
                <span>{props.equipment.nickName || props.equipment.model}</span>
              </span>

              {props.showChevron && (
                <BaseButton
                  className="equipment-telematic-description__chevron-right"
                  onClick={props.onClick}
                >
                  <ChevronRightIcon />
                </BaseButton>
              )}
            </div>
            <GridLayout
              rows={[{ height: '5' }, { height: '5' }]}
              cols={[{ width: '25%' }, { width: '75%' }]}
              className="inline-label-text"
            >
              <GridLayoutItem className="label">
                {t('equipment_model')}
              </GridLayoutItem>
              <GridLayoutItem className="text">
                {props.equipment.model}
              </GridLayoutItem>
              <GridLayoutItem className="label">
                {props.equipment.identifierType === 'Pin'
                  ? t('equipment_pin')
                  : t('equipment_serial')}
              </GridLayoutItem>
              <GridLayoutItem className="text">
                {props.equipment.pinOrSerial}
              </GridLayoutItem>
              <GridLayoutItem className="label">
                {t('equipment_hours')}
              </GridLayoutItem>
              <GridLayoutItem className="text">
                {getEngineHours()}
              </GridLayoutItem>
            </GridLayout>
          </div>
        </div>
      </CardBody>
      {props.equipment.hasTelematics && (
        <div className="equipment-list-details">
          <div className="row-4">
            <div
              className={
                !isEV
                  ? hasFuelOrDef
                    ? props.equipment.telematics?.defRemainingPercent != null &&
                      props.equipment.telematics?.fuelRemainingPercent != null
                      ? 'col-xxs-4'
                      : 'col-xxs-6'
                    : 'col-xxs-12'
                  : 'col-xxs-12'
              }
            >
              {isEV ? (
                <Card className={'ev-battery-card-small'}>
                  <span className="battery-svg-container">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20.001"
                      height="36"
                      viewBox="0 0 20.001 36"
                    >
                      <g id="bg_battery" fill="none">
                        <path
                          d="M2,36a2,2,0,0,1-2-2V3.687a2,2,0,0,1,2-2H6.111V1a1,1,0,0,1,1-1H12.89a1,1,0,0,1,1,1v.688H18a2,2,0,0,1,2,2V34a2,2,0,0,1-2,2Z"
                          stroke="none"
                        />
                        <rect
                          x={2}
                          y={36 - filledHeight - marginBottom}
                          width={16}
                          height={filledHeight}
                          stroke="none"
                          fill={fillColor}
                        />
                        {batteryStatusSvg}
                        <path
                          d="M 18.00090026855469 35 C 18.55219078063965 35 19.00069999694824 34.55109024047852 19.00069999694824 33.99929809570312 L 19.00069999694824 3.687299966812134 C 19.00069999694824 3.136009931564331 18.55219078063965 2.6875 18.00090026855469 2.6875 L 12.88969993591309 2.6875 L 12.88980007171631 1 L 7.111000061035156 0.9998999834060669 L 7.111000061035156 2.6875 L 1.999799966812134 2.6875 C 1.448510050773621 2.6875 1 3.136009931564331 1 3.687299966812134 L 1 33.99929809570312 C 1 34.55109024047852 1.448510050773621 35 1.999799966812134 35 L 18.00090026855469 35 M 18.00090026855469 36 L 1.999799966812134 36 C 0.8955000042915344 36 -1.525879866903779e-09 35.10449981689453 -1.525879866903779e-09 33.99929809570312 L -1.525879866903779e-09 3.687299966812134 C -1.525879866903779e-09 2.58299994468689 0.8955000042915344 1.6875 1.999799966812134 1.6875 L 6.111000061035156 1.6875 L 6.111000061035156 0.9998999834060669 C 6.111000061035156 0.447299987077713 6.559199810028076 0 7.111800193786621 0 L 12.88980007171631 0 C 13.44149971008301 0 13.88969993591309 0.447299987077713 13.88969993591309 0.9998999834060669 L 13.88969993591309 1.6875 L 18.00090026855469 1.6875 C 19.10519981384277 1.6875 20.00069999694824 2.58299994468689 20.00069999694824 3.687299966812134 L 20.00069999694824 33.99929809570312 C 20.00069999694824 35.10449981689453 19.10519981384277 36 18.00090026855469 36 Z"
                          stroke="none"
                          fill="#1a1a1a"
                        />
                      </g>
                    </svg>
                  </span>
                  {!batteryHasState ? (
                    <div className="ev-battery-state-info-small">
                      <span className="equipment-list-telematic-value">
                        {Math.round(
                          props.equipment.telematics?.electric?.battery
                            .remainingCharge || 0,
                        )}
                        %
                      </span>
                    </div>
                  ) : (
                    <div className="ev-battery-state-info-small">
                      <span className="equipment-list-telematic-value">
                        {Math.round(
                          props.equipment.telematics?.electric?.battery
                            .remainingCharge || 0,
                        )}
                        %
                      </span>
                      <span className="battery-status">
                        {batteryStatusState}
                      </span>
                    </div>
                  )}
                  {props.equipment.telematics?.electric?.remainingRange && (
                    <div className="ev-battery-duration-info-small">
                      <span>{t('range_remaining')}</span>
                      <span className="equipment-list-telematic-value">
                        {convertNumberToDistance(
                          props.equipment.telematics?.electric?.remainingRange
                            ?.distance ?? 0,
                          measurementUnit,
                        )}
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {formatSeconds(
                          props.equipment.telematics?.electric?.remainingRange
                            ?.time ?? 0,
                        )}
                      </span>
                    </div>
                  )}
                </Card>
              ) : (
                <Card className={'equipment-list-value_card'}>
                  {t('battery')}
                  <span className="equipment-list-telematic-value">
                    {(props.equipment.telematics?.extPowerVolts || 0) >=
                      12.5 && <img src={NormalBateryIcon} alt="React Logo" />}
                    {(props.equipment.telematics?.extPowerVolts || 0) >= 12.3 &&
                      (props.equipment.telematics?.extPowerVolts || 0) <
                        12.5 && (
                        <img src={WarningBateryIcon} alt="React Logo" />
                      )}
                    {(props.equipment.telematics?.extPowerVolts || 0) <
                      12.3 && (
                      <img src={CriticalBateryIcon} alt="React Logo" />
                    )}{' '}
                    <span>
                      {aproximateToTwo(
                        props.equipment.telematics?.extPowerVolts,
                      )}
                      V
                    </span>
                  </span>
                </Card>
              )}
            </div>
            {!isEV &&
              (props.equipment.telematics?.fuelRemainingPercent != null ||
                props.equipment.telematics?.defRemainingPercent != null) && (
                <>
                  {props.equipment.telematics?.fuelRemainingPercent != null && (
                    <div
                      className={
                        props.equipment.telematics.defRemainingPercent != null
                          ? 'col-xxs-4'
                          : 'col-xxs-6'
                      }
                    >
                      <Card className={'equipment-list-value_card'}>
                        {t('fuel')}
                        <span className="equipment-list-telematic-value">
                          <span
                            className={`equipment-list-semi-donut equipment-list-semi-donut-${Math.round(
                              props.equipment.telematics.fuelRemainingPercent,
                            )} equipment-list-semi-donut-${
                              props.equipment.telematics.fuelRemainingPercent >=
                              26
                                ? 'normal'
                                : props.equipment.telematics
                                    .fuelRemainingPercent >= 11
                                ? 'warning'
                                : 'critical'
                            }`}
                          />{' '}
                          <span>
                            {aproximateToTwo(
                              props.equipment.telematics.fuelRemainingPercent,
                            )}
                            %
                          </span>
                        </span>
                      </Card>
                    </div>
                  )}

                  {!isEV &&
                    props.equipment.telematics.defRemainingPercent != null && (
                      <div
                        className={
                          props.equipment.telematics.fuelRemainingPercent !=
                          null
                            ? 'col-xxs-4'
                            : 'col-xxs-6'
                        }
                      >
                        <Card className={'equipment-list-value_card'}>
                          {t('def')}
                          <span className="equipment-list-telematic-value">
                            <span
                              className={`equipment-list-semi-donut equipment-list-semi-donut-${Math.round(
                                props.equipment.telematics.defRemainingPercent,
                              )} equipment-list-semi-donut-${
                                props.equipment.telematics
                                  .defRemainingPercent >= 36
                                  ? 'normal'
                                  : props.equipment.telematics
                                      .defRemainingPercent >= 16
                                  ? 'warning'
                                  : 'critical'
                              }`}
                            />{' '}
                            <span>
                              {aproximateToTwo(
                                props.equipment.telematics.defRemainingPercent,
                              )}
                              %
                            </span>
                          </span>
                        </Card>
                      </div>
                    )}
                </>
              )}
          </div>

          <div className="row-4">
            <div className="col-xxs-3">
              <div className="row-2">
                <div className="col-xxs-6">
                  <Card>
                    <Button>
                      <RestartInhibitStatusIcon
                        telematics={props.equipment.telematics!}
                      />
                    </Button>
                  </Card>
                </div>
                <div className="col-xxs-6">
                  <Card>
                    <Button>
                      <InMotionStatusIcon
                        telematics={props.equipment.telematics!}
                      />
                    </Button>
                  </Card>
                </div>
              </div>
            </div>
            <div className={clsx('relative col-xxs-9')}>
              {!adressLoading ? (
                <Card
                  className={`r-geo-location-box ${
                    !adress ? 'unavailable-location-card' : ''
                  }`}
                >
                  <div className="row-2">
                    <div className="col-xxs-2 equipment-list-fault-codes-icon-wrapper">
                      <div className="geo-icon">
                        {adress ? <MapPinIcon /> : <MapUnavailablePinIcon />}
                      </div>
                    </div>
                    <div className="col-xxs-10">
                      <div
                        className="geo-address"
                        title={adress || t('location_unavailable1')}
                      >
                        {adress || t('location_unavailable1')}
                      </div>
                      {adress && <div className="geo-time">{getDate()}</div>}
                    </div>
                  </div>
                </Card>
              ) : (
                <LoadingOverlay isOpen text={t('loading_adress')} />
              )}
            </div>
          </div>
          {(props.equipment.telematics?.faultCodes?.length || 0) > 0 && (
            <div className="errors-box">
              <div className="row-2 equipment-list-fault-codes-box">
                <div className="col-xxs-2 equipment-list-fault-codes-icon-wrapper">
                  <span className="equipment-list-fault-codes-icon">
                    <img src={AssetTriangleIcon} />
                    <span className="equipment-list-fault-codes-text">
                      {props.equipment.telematics!.faultCodes?.length || 0}
                    </span>
                  </span>
                </div>
                <div className="col-xxs-10">
                  <span className="alert">
                    {[...(props.equipment.telematics!.faultCodes || [])]
                      .sort((a, b) =>
                        Date.parse(a.timeReported) < Date.parse(b.timeReported)
                          ? -1
                          : Date.parse(a.timeReported) >
                            Date.parse(b.timeReported)
                          ? 1
                          : 0,
                      )
                      ?.map((faultCode, index) => {
                        if (index < 2) {
                          if (faultCode.j1939Fmi || faultCode.j1939Spn) {
                            return (
                              <div key={index} className="ellipsis">
                                <span>
                                  J1939: {faultCode.j1939Spn}/
                                  {faultCode.j1939Fmi}
                                </span>{' '}
                                -{' '}
                                {faultCode.description ||
                                  t('no_description_available')}
                              </div>
                            );
                          } else {
                            return (
                              <div className="ellipsis" key={index}>
                                <span>E:{faultCode.code}</span> -{' '}
                                {faultCode.description ||
                                  t('no_description_available')}
                              </div>
                            );
                          }
                        }
                        if (index === 2) {
                          return <div key={index}>{t('and_more')}</div>;
                        }
                        return null;
                      })}
                  </span>
                </div>
              </div>
            </div>
          )}

          <StarterStatusRow telematics={props.equipment.telematics} />

          {isSubscriptionsFeatureEnabled && (
            <>
              {!subscriptionInfo.hasPremium && (
                <SubscriptionRow
                  label={t('subscriptions.equipment_card.unlock_features')}
                  buttonLabel={t('upgrade')}
                  onClick={() =>
                    openManageYourPlanDialog({ equipment: props.equipment })
                  }
                />
              )}

              {subscriptionInfo.endsWithinDays && (
                <SubscriptionRow
                  label={t(
                    `subscriptions.equipment_card.${
                      subscriptionInfo.isInitial ? 'initial' : 'premium'
                    }_ends_on`,
                    { date: subscriptionInfo.endsOn },
                  )}
                  buttonLabel={t('renew')}
                  onClick={() =>
                    openManageYourPlanDialog({
                      equipment: props.equipment,
                    })
                  }
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
